import { useState, useMemo } from "react";
import { format, parseISO } from "date-fns";
import EntityInformationModal from "../../components/modals/EntityInformationModal";
import EntityCalendar from "../../components/EntityCalendar";
import { routeProgressLabel } from "../../utils";

// Work hours are seconds from midnight, the same encoding the Shifts screen uses.
const formatWorkTime = (sec) => {
    if (sec == null) return "-";
    const d = new Date(0);
    d.setSeconds(sec);
    let h = d.getUTCHours();
    const m = d.getUTCMinutes().toString().padStart(2, "0");
    const ampm = h >= 12 ? "pm" : "am";
    h = h % 12 || 12;
    return `${h}:${m} ${ampm}`;
};

const JobSchedulesCalendar = ({
    jobSchedules,
    onRefresh,
    filterText = "",
    onEditJob,
}) => {
    const [modalJob, setModalJob] = useState(null);

    const getEventColor = (schedule) => {
        const now = new Date();
        const startDate = parseISO(schedule.start_time);
        const endDate = parseISO(schedule.end_time);
        if (now < startDate) return "#3b82f6";
        if (now > endDate) return "#6b7280";
        return "#10b981";
    };

    const calendarEvents = useMemo(() => {
        if (!jobSchedules) return [];

        const q = filterText?.trim().toLowerCase();
        const filteredSchedules = !q
            ? jobSchedules
            : jobSchedules.filter(
                  (schedule) =>
                      String(schedule.name || "")
                          .toLowerCase()
                          .includes(q) ||
                      String(schedule.description || "")
                          .toLowerCase()
                          .includes(q) ||
                      String(schedule.part?.name || "")
                          .toLowerCase()
                          .includes(q) ||
                      (schedule.operations || []).some((op) =>
                          String(op.operation?.name || "")
                              .toLowerCase()
                              .includes(q),
                      ) ||
                      String(schedule.device_id || "")
                          .toLowerCase()
                          .includes(q),
              );

        // A run that spans days stays one timed event: FullCalendar splits it per day
        // column in the week/day grids and draws one bar across the month grid.
        return filteredSchedules.map((schedule) => ({
            id: schedule.id.toString(),
            title: `${schedule.device_id} - ${schedule.part?.name || "N/A"}`,
            start: parseISO(schedule.start_time),
            end: parseISO(schedule.end_time),
            color: getEventColor(schedule),
            extendedProps: { schedule },
        }));
    }, [jobSchedules, filterText]);

    const handleEventClick = (eventInfo) => {
        const schedule = eventInfo.event.extendedProps.schedule;
        setModalJob(schedule);
    };

    return (
        <>
            <EntityCalendar
                events={calendarEvents}
                eventClick={handleEventClick}
            />
            <EntityInformationModal
                show={!!modalJob}
                onHide={() => setModalJob(null)}
                title={modalJob?.name}
                onEdit={() => {
                    if (onEditJob) {
                        onEditJob(modalJob);
                    }
                    setModalJob(null);
                }}
                isEditing={false}
                editTitle="Edit Job"
            >
                <div className="tw-flex tw-flex-row tw-row-span-2">
                    <div className="tw-flex-1">
                        <span className="tw-font-medium">Part:</span>{" "}
                        {modalJob?.part?.name}
                    </div>
                    <div className="tw-flex-1">
                        <span className="tw-font-medium">Progress:</span>{" "}
                        {routeProgressLabel(modalJob) || "No route"}
                    </div>
                </div>
                <div className="tw-flex tw-flex-row tw-row-span-2">
                    <div className="tw-flex-1">
                        <span className="tw-font-medium">From:</span>{" "}
                        {modalJob &&
                            format(parseISO(modalJob.start_time), "PPpp")}
                    </div>
                    <div className="tw-flex-1">
                        <span className="tw-font-medium">To:</span>{" "}
                        {modalJob &&
                            format(parseISO(modalJob.end_time), "PPpp")}
                    </div>
                </div>
                <div>
                    <span className="tw-font-medium">Shift:</span>{" "}
                    {modalJob?.shift
                        ? `${modalJob.shift.name} (${formatWorkTime(modalJob.shift.start_time)} - ${formatWorkTime(modalJob.shift.end_time)})`
                        : "Full range"}
                </div>
                {modalJob?.description && (
                    <div>
                        <span className="tw-font-medium">Description:</span>{" "}
                        {modalJob.description}
                    </div>
                )}
            </EntityInformationModal>
        </>
    );
};

export default JobSchedulesCalendar;
